import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useRevenueByMonth, useSalesUsers } from '../hooks';

/**
 * "Expected revenue by month" (#1409): one bucket per month of the window
 * (empty months included), each listing the subscriptions renewing that month
 * with their annual value and where the renewal stands (paid > invoiced >
 * quoted > due). Overdue renewals stay at their past month on purpose — the
 * point of the view is to surface what is late, not to hide it.
 */

const STATUS_CLASS: Record<string, string> = {
  paid: 'bg-success/10 text-success',
  invoiced: 'bg-warning/10 text-warning',
  quoted: 'bg-info/10 text-info',
  due: 'bg-muted text-muted-foreground',
};

export function yearMonth(offsetFromToday = 0): string {
  const d = new Date();
  d.setDate(1); // month-end days must not overflow into the next month (Oct 31 + 11 → Oct, not Sep)
  d.setMonth(d.getMonth() + offsetFromToday);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function RevenueByMonth() {
  const { t } = useTranslation('subscriptions');
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(yearMonth());
  const [to, setTo] = useState(yearMonth(11));
  const [salesManagerId, setSalesManagerId] = useState('');
  const salesUsers = useSalesUsers();
  const { data, isLoading, error } = useRevenueByMonth(from, to, salesManagerId || undefined, open);

  return (
    <div className="rounded-lg border bg-card p-4 space-y-4" data-testid="revenue-by-month">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('revenueByMonth.title', 'Expected revenue by month')}</h2>
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)} data-testid="revenue-by-month-toggle">
          {open ? t('revenueByMonth.hide', 'Hide') : t('revenueByMonth.show', 'Show expected revenue')}
        </Button>
      </div>

      {open && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block space-y-1 text-xs text-muted-foreground">
              <span>{t('revenueByMonth.from', 'From')}</span>
              <Input
                type="month"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-36"
                data-testid="revenue-from"
              />
            </label>
            <label className="block space-y-1 text-xs text-muted-foreground">
              <span>{t('revenueByMonth.to', 'To')}</span>
              <Input
                type="month"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-36"
                data-testid="revenue-to"
              />
            </label>
            <label className="block space-y-1 text-xs text-muted-foreground">
              <span>{t('revenueByMonth.accountManager', 'Account manager')}</span>
              <select
                aria-label={t('revenueByMonth.accountManager', 'Account manager')}
                value={salesManagerId}
                onChange={(e) => setSalesManagerId(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="revenue-am"
              >
                <option value="">{t('revenueByMonth.allManagers', 'All account managers')}</option>
                {(salesUsers.data ?? []).map((u) => (
                  <option key={u._id} value={u._id}>
                    {`${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {isLoading && <Skeleton className="h-24 w-full" />}
          {error && (
            <p className="text-sm text-danger" data-testid="revenue-error">
              {t('revenueByMonth.error', 'Could not load the expected revenue.')}
            </p>
          )}
          {data && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('revenueByMonth.subscription', 'Subscription')}</TableHead>
                  <TableHead>{t('revenueByMonth.account', 'Account')}</TableHead>
                  <TableHead>{t('revenueByMonth.manager', 'Manager')}</TableHead>
                  <TableHead className="text-right">{t('revenueByMonth.amount', 'Amount')}</TableHead>
                  <TableHead>{t('revenueByMonth.statusHeader', 'Status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.months.map((bucket) => (
                  <Fragment key={bucket.month}>
                    <TableRow className="bg-muted/50 font-medium" data-testid={`revenue-month-${bucket.month}`}>
                      <TableCell colSpan={3}>{bucket.month}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {/* one amount per currency — a merged cross-currency sum is a made-up number */}
                        {Object.entries(bucket.totals)
                          .sort(([a], [b]) => (a === 'CAD' ? -1 : b === 'CAD' ? 1 : a.localeCompare(b)))
                          .map(([currency, amount]) => formatCurrency(amount, currency))
                          .join(' + ') || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {t('revenueByMonth.subscriptionsCount', { count: bucket.items.length, defaultValue: '{{count}} subscription' })}
                      </TableCell>
                    </TableRow>
                    {bucket.items.map((item, i) => (
                      <TableRow key={`${bucket.month}-${item.subscriptionId ?? i}`} data-testid={`revenue-item-${bucket.month}-${i}`}>
                        <TableCell className="tabular-nums">{item.subscriptionNumber ?? '—'}</TableCell>
                        <TableCell>{item.accountName ?? '—'}</TableCell>
                        <TableCell className="text-muted-foreground">{item.ownerName ?? '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.amount, item.currency)}</TableCell>
                        <TableCell>
                          <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_CLASS[item.status] ?? STATUS_CLASS.due}`}>
                            {t(`revenueByMonth.status.${item.status}`, item.status)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}
